import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const StudyAbroadCostSection = () => {
  const featureSectionRef = useRef(null);
  const costButtonSectionRef = useRef(null);
  
    // Use useLayoutEffect to ensure the DOM is ready
    useGSAP(() => {
      // Register GSAP plugins
      gsap.registerPlugin(ScrollTrigger);
  
      // GSAP scroll-triggered animation for .line elements
      gsap.from(featureSectionRef.current, {
        y: 100,         // Initial Y offset
        opacity: 0,     // Start with opacity 0
        duration: 1.5,    // Duration of the animation
        stagger: 0.1,   // Stagger delay between elements
        ease: "power3.out", // Easing for smoothness
        scrollTrigger: {
          trigger: "#about",  // Trigger animation when this element comes into view
          start: "top 80%",  // Start when top of the section is at 80% of viewport height
        }
      });
    }, []);



  useEffect(() => {
    // GSAP scroll animation for rotating cross image
    gsap.to('#rotating-cross', {
      rotation: 90,
      ease: 'none',
      scrollTrigger: {
        trigger: '#rotating-cross',
        start: 'top bottom',
        end: 'bottom top',
        scrub: true, // Smooth sync with scroll
      },
    });
  }, []);

  return (
    <>
      {/* First Section with Staggered Text and Service Info */}
      <section id="about" className="bg-white text-black pl-10 py-16 px-0 md:px-32 light-section">
        <div className="max-w-7xl text-center mx-auto overflow-hidden">
          <h1 ref={featureSectionRef} className="text-8xl font-bold mt-40 tracking-tighter">
            <div className="">
              YOUR <span className="text-[#457b9d]">GLOBAL EDUCATION</span>
            </div>
            <div className="">BEGINS WITH SOCRATICALI</div>
          </h1>

          <p className="mt-14 text-[24px] tracking-tight">
            SocraticAli Consultants makes studying abroad simple. From choosing the right university to securing
            scholarships and visas, we provide seamless support with no upfront fees. Our expert team is here to guide
            you every step of the way, ensuring your academic goals are within reach.
          </p>
        </div>

        {/* Service Cards */}
        <div id='activities' className="max-w-7xl mx-auto mt-20 grid grid-cols-1 md:grid-cols-3 gap-20 mb-40">
          {[
            {
              imgSrc: '/Assets/noun-website-7473589.png',
              title: 'University & Course Selection',
              description:
                'Choosing the right university and program can be overwhelming. We provide personalized recommendations based on your academic background, interests, and career goals to ensure the best fit for your future.',
            },
            {
              imgSrc: '/Assets/noun-assistance-7213587.png',
              title: 'Application Assistance',
              description:
                'A strong application increases your chances of acceptance. We help you with document preparation, personal statements, recommendation letters, and application submission to make the process smooth and error-free.',
            },
            {
              imgSrc: '/Assets/noun-scholarship-7303842.png',
              title: 'Scholarship Guidance',
              description:
                'Studying abroad can be expensive, but we help you find and apply for scholarships that match your profile, ensuring you get the financial support you need.',
            },
            {
              imgSrc: '/Assets/noun-visa-7290388.png',
              title: 'Visa Support',
              description:
                'The visa process can be complex, but we simplify it for you. From preparing necessary documents to interview coaching, we guide you at every step to increase your chances of approval.',
            },
            {
              imgSrc: '/Assets/noun-assistance-7695723.png',
              title: 'Online Portal',
              description:
                'Your one-stop solution for booking free consultations, checking university fees, and applying to universities. We ensures you have everything you need to make informed decisions and navigate your academic path smoothly.',
            },
            {
              imgSrc: '/Assets/noun-consultancy-506652.png',
              title: 'Digital Consultancy',
              description:
                'No need to visit an office! Our digital consultancy service allows students from anywhere in Pakistan to receive expert guidance via online sessions, making the process convenient and accessible.',
            },
            {
              imgSrc: '/Assets/noun-assistance-7695723.png',
              title: 'Pre-Departure Assistance',
              description:
                'Moving to a new country can be challenging. We help with accommodation options, travel arrangements, and cultural orientation to ensure a smooth transition.',
            },
            {
              imgSrc: '/Assets/noun-solution-7745845.png',
              title: 'One-Window Solution',
              description:
                'From initial counselling to final departure, we provide all services under one roof, eliminating the hassle of dealing with multiple agencies.',
            },
          ].map((service, index) => (
            <div key={index}>
              <img src={service.imgSrc} alt={service.title} className='size-24'/>
              <h3 className="font-bold text-[24px] mt-4 tracking-tight">{service.title}</h3>
              <p className="mt-4 text-gray-900 text-[20px] leading-6 tracking-tight">{service.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Second Section with Rotating Cross and How Much Does It Cost */}
      <section id='calculate-cost' className="relative bg-[#1d3557] min-h-screen flex flex-col justify-center items-center text-center p-10">
        {/* Rotating Cross SVG */}
        <img
          src="/Assets/crosss-removebg-preview.png"
          alt="Rotating Cross"
          id="rotating-cross"
          className="absolute -top-[243px] -right-52 w-100 h-100"
        />

        {/* Content */}
        <div ref={costButtonSectionRef} className="cost-lines px-0 md:px-20  overflow-hidden">
          <h1 className="text-white text-[110px] font-semibold tracking-tighter leading-[1]">
            <div className="">HOW MUCH DOES IT</div>
            <div className="">COST?</div>
          </h1>
        </div>

        <p className="text-white text-[20px] mt-4 max-w-4xl">
          Use the <strong>SocraticAli Cost Calculator</strong> to estimate the expenses for your study abroad journey.
          <br />
          It is our most frequently asked question.
          <br />
          <strong>Get your answer instantly!</strong>
        </p>

        <a href='#calculator' className="mt-6 bg-[#457b9d] text-white font-bold px-6 py-3 rounded-md shadow-lg hover:bg-[#1d3557] hover:text-white transition-all duration-300">
          CALCULATE COST
        </a>
      </section>
    </>
  );
};

export default StudyAbroadCostSection;
